import type { Draft } from '../lib/index.ts';

/**
 * Innloggingen i portalen (tech-decisions.md, «Portalen», låst): leietakeren
 * autentiserer på SIN side, utsteder feedback-tokenet og sender brukeren tilbake
 * med tokenet i URL-FRAGMENTET. Portalen tar det ut, legger det i
 * `sessionStorage` og fjerner fragmentet med `history.replaceState` straks —
 * aldri `localStorage`, aldri sti eller query, aldri i historikk. Tokenet bærer
 * ingen PII og lever i 15 minutter, men er et bærertoken og behandles som ett.
 *
 * Utkastet (teksten innsenderen skrev da tokenet gikk ut) ligger også i
 * `sessionStorage`, så «Logg inn på nytt» kan ta en runde hos leietakeren uten
 * at teksten går tapt.
 */

const TOKEN_NØKKEL = 'innspel:token';
const UTKAST_NØKKEL = 'innspel:utkast';
const INVITASJON_NØKKEL = 'innspel:invitasjon';
const TV_FJERNEDE_NØKKEL = 'innspel:tv-fjernede';
const FRAGMENT_NØKKEL = 'token';
const INVITASJON_FRAGMENT = 'inv';

export interface Sesjon {
  /** Tokenet slik det skal i Authorization, eller null uten innlogging. */
  token(): string | null;
  /** Utløpstid fra tokenets `exp`, eller null. Serveren avgjør; dette er bare til visning. */
  utloperOm(): number | null;
  loggUt(): void;
  lagreUtkast(u: Draft): void;
  hentUtkast(): Draft | null;
  /** Engangs-tokenet fra leietakerens invitasjonslenke, eller null. Brukes én gang, av aksepten (DD-12). */
  invitasjonstoken(): string | null;
  glemInvitasjon(): void;
  /** URL-en «Logg inn hos …» sender til, med portalens retur-URL som `return`. */
  innloggingsUrl(loginRedirectUrl: string, retur: string): string;
  /** TV-linjene innsenderen fjernet før «Logg inn hos …» på `/h/<id>` (S1.3), så de ikke kommer tilbake etter redirecten. */
  lagreFjernede(handoffId: string, felt: string[]): void;
  /** Linjene for DENNE overleveringen, eller []. En annen id gir [] — en ny QR er en ny økt. */
  hentFjernede(handoffId: string): string[];
}

/**
 * Leser `#token=…` fra URL-en, legger det i `sessionStorage` og fjerner
 * fragmentet — én gang, ved innlasting. Returnerer om et token ble tatt ut.
 */
export function taTokenFraFragment(win: Pick<Window, 'location' | 'history'> = window, lagring: Storage = sessionStorage): boolean {
  const hash = win.location.hash.startsWith('#') ? win.location.hash.slice(1) : win.location.hash;
  if (!hash) return false;
  const params = new URLSearchParams(hash);
  const token = params.get(FRAGMENT_NØKKEL);
  if (!token) return false;

  lagring.setItem(TOKEN_NØKKEL, token);
  // Fragmentet er borte fra adresselinja og fra historikken — replaceState, ikke pushState.
  win.history.replaceState(win.history.state, '', win.location.pathname + win.location.search);
  return true;
}

/**
 * Leser `#inv=…` fra URL-en — invitasjonens engangstoken, som leietakeren sendte i
 * SIN kanal (DD-12). Samme regel som feedback-tokenet: `sessionStorage`, aldri
 * `localStorage`, og fragmentet fjernes straks så tokenet ikke blir liggende i
 * adresselinja eller historikken. Kan komme i samme fragment som `token`.
 */
export function taInvitasjonFraFragment(win: Pick<Window, 'location' | 'history'> = window, lagring: Storage = sessionStorage): boolean {
  const hash = win.location.hash.startsWith('#') ? win.location.hash.slice(1) : win.location.hash;
  if (!hash) return false;
  const params = new URLSearchParams(hash);
  const invitasjon = params.get(INVITASJON_FRAGMENT);
  if (!invitasjon) return false;

  lagring.setItem(INVITASJON_NØKKEL, invitasjon);
  // Bare `inv` fjernes: feedback-tokenet kan ligge i SAMME fragment, og det tas ut
  // av `taTokenFraFragment` rett etter. Blir fragmentet tomt, forsvinner det helt.
  params.delete(INVITASJON_FRAGMENT);
  const rest = params.toString();
  win.history.replaceState(win.history.state, '', win.location.pathname + win.location.search + (rest ? `#${rest}` : ''));
  return true;
}

export function lagSesjon(lagring: Storage = sessionStorage, nå: () => number = Date.now): Sesjon {
  const les = (): string | null => {
    try {
      return lagring.getItem(TOKEN_NØKKEL);
    } catch {
      return null;
    }
  };
  return {
    token: les,
    utloperOm() {
      const t = les();
      const exp = t ? dekodPayload(t)?.exp : undefined;
      return typeof exp === 'number' ? exp * 1000 - nå() : null;
    },
    loggUt() {
      try {
        lagring.removeItem(TOKEN_NØKKEL);
      } catch {
        /* ingenting å fjerne */
      }
    },
    lagreUtkast(u) {
      lagring.setItem(UTKAST_NØKKEL, JSON.stringify(u));
    },
    hentUtkast() {
      const rå = lagring.getItem(UTKAST_NØKKEL);
      if (!rå) return null;
      lagring.removeItem(UTKAST_NØKKEL);
      try {
        const u = JSON.parse(rå) as Partial<Draft>;
        return u && (u.track === 'feil' || u.track === 'onske') && typeof u.text === 'string' ? { track: u.track, text: u.text } : null;
      } catch {
        return null;
      }
    },
    invitasjonstoken() {
      try {
        return lagring.getItem(INVITASJON_NØKKEL);
      } catch {
        return null;
      }
    },
    glemInvitasjon() {
      try {
        lagring.removeItem(INVITASJON_NØKKEL);
      } catch {
        /* ingenting å fjerne */
      }
    },
    innloggingsUrl(loginRedirectUrl, retur) {
      const url = new URL(loginRedirectUrl);
      url.searchParams.set('return', retur);
      return url.toString();
    },
    lagreFjernede(handoffId, felt) {
      try {
        lagring.setItem(TV_FJERNEDE_NØKKEL, JSON.stringify({ id: handoffId, felt }));
      } catch {
        /* uten lagring kommer linjene tilbake etter innloggingen — innsenderen ser dem og kan fjerne igjen */
      }
    },
    hentFjernede(handoffId) {
      try {
        const rå = JSON.parse(lagring.getItem(TV_FJERNEDE_NØKKEL) ?? 'null') as { id?: unknown; felt?: unknown } | null;
        return rå?.id === handoffId && Array.isArray(rå.felt) ? rå.felt.filter((f): f is string => typeof f === 'string') : [];
      } catch {
        return [];
      }
    },
  };
}

/** JWT-payloaden, uten verifisering — den gjør serveren. Kun til visning. */
function dekodPayload(token: string): Record<string, unknown> | null {
  const del = token.split('.')[1];
  if (!del) return null;
  try {
    const b64 = del.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '='));
    const p: unknown = JSON.parse(json);
    return p && typeof p === 'object' ? (p as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

